import { useEffect, useState } from "react";
import { http } from "../../lib/api";
import { fValor } from "../../lib/trading";
import { TradingPanel } from "./Panel";
import { TC, TvChart } from "./TvChart";
import { M, P2, agrupar, lDia, lMes, linea } from "./barras";

const selC = "trd-mono rounded-md border border-[var(--trd-border)] bg-[var(--trd-surface)] px-2 py-1 text-[11px] text-[#C9D1D9]";
const F0 = { desde: "", hasta: "", plazo: "", tipo: "", referenciador_id: "", ejecutivo_id: "" };

const Filtros = ({ f, setF, o, meses }) => {
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const Sel = ({ k, l, opts }) => <select value={f[k]} onChange={s(k)} className={selC} data-testid={`cmp-f-${k}`} aria-label={l}><option value="">{l}</option>{opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select>;
  const ms = meses.map((m) => [m, lMes(m)]);
  return (
    <div className="trd flex flex-wrap items-center gap-1.5 rounded-xl border border-[var(--trd-border)] p-2" data-testid="cmp-filtros">
      <Sel k="desde" l="Desde" opts={ms} /><Sel k="hasta" l="Hasta" opts={ms} />
      <Sel k="plazo" l="Plazo: todos" opts={(o?.plazos || []).map((p) => [p, p])} /><Sel k="tipo" l="PF y PM" opts={[["PF", "PF"], ["PM", "PM"]]} />
      <Sel k="referenciador_id" l="Referenciador: todos" opts={(o?.referenciadores || []).map((r) => [r.id, r.nombre])} />
      <Sel k="ejecutivo_id" l="Ejecutivo: todos" opts={(o?.ejecutivos || []).map((r) => [r.id, r.nombre])} />
      {Object.values(f).some(Boolean) && <button type="button" onClick={() => setF(F0)} className="trd-mono px-2 text-[11px] text-[var(--trd-accent)]" data-testid="cmp-f-limpiar">Limpiar</button>}
    </div>
  );
};

const CarteraVsCaptacion = ({ d, persona }) => {
  const ms = d.meses, g = agrupar(ms.map((m) => lMes(m.mes)), [{ id: "cartera", name: "Cartera", color: TC.up, vals: ms.map((m) => m.cartera), fmt: M }, { id: "captacion", name: "Captación", color: TC.blue, vals: ms.map((m) => m.captacion), fmt: M }]);
  const ltd = ms.flatMap((m) => [m.ltd, null, null]);
  const series = [...g.series, { id: "ltd", name: "Cartera / Captación", type: "line", points: true, scale: "left", color: TC.accent, data: linea(ltd), fmt: P2 },
    ...(persona ? [{ id: "coloc", name: "Colocación del mes", type: "line", points: true, dashed: true, color: TC.pink, data: linea(ms.flatMap((m) => [m.colocacion, null, null])), fmt: M }] : [])];
  const u = [...ms].reverse().find((m) => m.ltd != null);
  return (
    <TradingPanel title="Cartera vs Captación por mes · Cartera / Captación %" ultimo={u ? `LTD ${P2(u.ltd)}` : "LTD —"} cambio={u ? `Cartera al ${lDia(u.cartera_corte)} · Captación al ${lDia(u.captacion_corte)}` : null} tono="flat" testid="cmp-ltd"
      como={d.como.ltd} fuente="Loan Management + Captación (cortes PISA)">
      <TvChart series={series} labels={g.labels} height={260} fmt={M} toggle testid="cmp-ltd-chart" />
      <p className="mt-1 text-[10px] text-[var(--trd-accent)]" data-testid="cmp-ltd-nota">{d.nota}{d.sin_asignar_pct != null && persona ? ` · Captación sin asignar: ${P2(d.sin_asignar_pct)} del saldo (no se atribuye a ninguna persona).` : ""}</p>
    </TradingPanel>
  );
};

const Spread = ({ d }) => {
  const ms = d.meses;
  const series = [{ id: "cartera", name: "Tasa cartera", type: "area", color: TC.up, data: linea(ms.map((m) => m.tasa_cartera)), fmt: P2 },
    { id: "tiie", name: "TIIE 28", type: "area", color: TC.accent, fill: TC.bg, data: linea(ms.map((m) => m.tiie)), fmt: P2 },
    { id: "plazo", name: "Costo captación plazo", type: "line", points: true, color: TC.blue, data: linea(ms.map((m) => m.costo_plazo)), fmt: P2 },
    { id: "total", name: "Costo captación total", type: "line", points: true, color: TC.cyan, data: linea(ms.map((m) => m.costo_total)), fmt: P2 }];
  const u = [...ms].reverse().find((m) => m.costo_total != null && m.tasa_cartera != null);
  return (
    <TradingPanel title="Spread · tasa de cartera vs costo de captación" ultimo={u ? `Spread ${((u.tasa_cartera - u.costo_total) * 100).toFixed(2)} pp` : "—"} cambio={u ? `con costo plazo ${((u.tasa_cartera - u.costo_plazo) * 100).toFixed(2)} pp` : null} tono="flat" testid="cmp-spread"
      como={`${d.como.spread} Área sombreada: tasa de cartera sobre TIIE 28.`} fuente="Loan Management + Captación + DOF">
      <TvChart series={series} labels={ms.map((m) => lMes(m.mes))} height={240} fmt={P2} toggle testid="cmp-spread-chart" />
    </TradingPanel>
  );
};

const Flujos = ({ fl }) => {
  const [iv, setIv] = useState("S");
  const b = iv === "S" ? fl.semanas : fl.meses;
  const series = [{ id: "ent", name: "Entradas cartera", type: "histogram", color: TC.up, data: b.map((x) => ({ value: x.entradas })), fmt: M },
    { id: "sal", name: "Salidas captación", type: "histogram", color: TC.down, data: b.map((x) => ({ value: -x.salidas })), fmt: M },
    { id: "neto", name: "Brecha neta", type: "line", points: true, color: TC.accent, data: b.map((x) => ({ value: x.neto })), fmt: M },
    { id: "acum", name: "Acumulada", type: "line", dashed: true, color: TC.violet, data: b.map((x) => ({ value: x.acumulado })), fmt: M }];
  return (
    <TradingPanel title={`Flujos de liquidez desde el día 0 (${lDia(fl.dia0)}) · ${iv === "S" ? "13 semanas" : "12 meses"}`} ultimo={fValor(b[b.length - 1]?.acumulado, "money", true)} cambio="acumulada" tono={b[b.length - 1]?.acumulado < 0 ? "bad" : "good"}
      iv={iv} setIv={(x) => setIv(x === "M" ? "M" : "S")} testid="cmp-flujos" como={fl.como} fuente="Liquidez / ALM (escenario base)">
      <TvChart series={series} labels={b.map((x) => (iv === "S" ? lDia(x.inicio) : lMes(x.mes)))} height={240} fmt={M} toggle testid="cmp-flujos-chart" />
    </TradingPanel>
  );
};

export const Comparativo = () => {
  const [f, setF] = useState(F0);
  const [d, setD] = useState(null);
  const [o, setO] = useState(null);
  const [meses, setMeses] = useState([]);
  useEffect(() => { http.get("/comparativo/opciones").then((r) => setO(r.data)).catch(() => setO(null)); }, []);
  useEffect(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    http.get("/comparativo", { params: p }).then((r) => { setD(r.data); if (!meses.length) setMeses(r.data.meses.map((m) => m.mes)); }).catch(() => setD(false));
  }, [JSON.stringify(f)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (d === false) return <p className="trd rounded-2xl p-4 text-xs text-[var(--trd-muted)]" data-testid="cmp-error">Sin acceso al comparativo.</p>;
  if (!d) return <div className="trd h-[300px] animate-pulse rounded-2xl" />;
  return (
    <div className="space-y-3" data-testid="comparativo">
      <Filtros f={f} setF={setF} o={o} meses={meses} />
      <CarteraVsCaptacion d={d} persona={!!(f.referenciador_id || f.ejecutivo_id)} />
      <div className="grid gap-3 lg:grid-cols-2"><Spread d={d} /><Flujos fl={d.flujos} /></div>
    </div>
  );
};
